import React, {MouseEventHandler, useEffect, useMemo, useState} from 'react';
import {Link, LinkProps} from 'react-router-dom';
import {useFormContext} from 'react-hook-form';
import clsx from 'clsx';
import {noop} from '@utils/constants';
import styles from './Button.module.scss';

type HTMLButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
type HTMLAnchorProps = LinkProps;

export type BadgeColor =
    'white' |
    'black' |
    'red' |
    'grey-100' |
    'grey-200' |
    'grey-300' |
    'grey-400' |
    'blue' |
    'blue-light' |
    'blue-dark' |
    'orange' |
    'green' |
    'yellow' |
    'color-vk';

interface TProps<AS = 'button' | 'a'> extends Partial<{
    hover: boolean,
    active: boolean,
    focus: boolean,
}> {
    as?: AS
}

const Spinner = () => <div className={styles.spinner}></div>;

export type ButtonProps = (HTMLButtonProps & TProps<'button'>) | (HTMLAnchorProps & TProps<'a'>);

const buttonClassName = (classes: string, {
    hover,
    active,
    focus,
}: Partial<Record<'hover' | 'active' | 'focus', boolean>>) => clsx({
    [styles.hover]: hover,
    [styles.active]: active,
    [styles.focus]: focus,
}, `t1 ${styles.ButtonBase} ${classes}`);

const makeButton = (_buttonClassName = '') => ({
                                                   children,
                                                   className,
                                                   hover,
                                                   active,
                                                   focus,
                                                   as = 'button',
                                                   ...props
                                               }: ButtonProps) => {
    const As = useMemo(() => {
        if (as !== 'a') {
            return as;
        } else {
            return Link;
        }
    }, [as]);
    const [isPending, setIsPending] = useState(false);
    const form = useFormContext();
    const [formValue, setFormValue] = useState(form?.watch());

    // @ts-ignore
    const formError = form?.getFieldState('_')?.error?.[0];

    useEffect(() => {
        if (!form || (props.role !== 'submit')) {
            return;
        }
        setIsPending(form.formState.isSubmitting);
    }, [form?.formState?.isSubmitting, props.role]);

    useEffect(() => {
        if (JSON.stringify(formValue) !== JSON.stringify(form?.watch())) {
            form.clearErrors('_');
            setFormValue(form.watch());
        }
    }, [JSON.stringify(form?.watch())]);

    return <div className={`d-flex flex-column ${className}`}>
        {/*@ts-ignore*/}
        <As
            className={buttonClassName(_buttonClassName, {
                hover,
                active,
                focus,
            })}
            {...props}
        >
            <div className='d-flex align-items-center gap-6'>
                {isPending && <>&nbsp;<Spinner/>&nbsp;</>}
                {!isPending && children}
            </div>
        </As>
        {props.role === 'submit' && formError && <span className='t4 text-negative-100'>
            {formError}
        </span>}
    </div>;
};

export const ButtonPrimary = makeButton(styles.ButtonPrimary);
export const ButtonPrimaryCompact = makeButton(styles.ButtonPrimaryCompact);
export const ButtonSecondary = makeButton(styles.ButtonSecondary);
export const ButtonTernary = makeButton(styles.ButtonTernary);

export const ButtonBlack = makeButton(styles.ButtonBlack);

type ButtonIconProps = ButtonProps & {
    color?: BadgeColor;
    colorHover?: BadgeColor;
};
const ButtonIconInner = makeButton(styles.ButtonIcon);
export const ButtonIcon = ({
                               color,
                               colorHover,
                               ...props
                           }: ButtonIconProps) => <ButtonIconInner
    style={{
        // @ts-ignore
        '--color': `var(--${color})`,
        '--color-hover': `var(--${colorHover})`,
    }}
    {...props}
/>;

type ButtonToggleProps = Omit<ButtonProps, 'children'> & {
    enabledContent: any;
    disabledContent: any;
    name: string;
}

export const ButtonToggle = ({
                                 enabledContent,
                                 disabledContent,
                                 name,
                                 onClick = noop,
                                 ...props
                             }: ButtonToggleProps) => {

    const form = useFormContext();
    const enabled = form.watch(name);

    const handleClick: MouseEventHandler<HTMLButtonElement> = e => {
        e.preventDefault();
        e.stopPropagation();
        form.setValue(name, !enabled);
        onClick(e as any);
    };

    // @ts-ignore
    return <ButtonSecondary as='button' {...props} onClick={handleClick}>
        {enabled && enabledContent}
        {!enabled && disabledContent}
    </ButtonSecondary>;
};

export type TButtonComponent =
    typeof ButtonPrimary |
    typeof ButtonPrimaryCompact |
    typeof ButtonSecondary |
    typeof ButtonTernary |
    typeof ButtonIcon;
